import { InputRange, Packaging, ToolTip } from '@/components';
import { useAlert, useCart, useClient, useCorporateFlags, useDiscountAmountFlex, useDiscountFlex, useGlobals, useTables, useUserContext } from '@/hooks';
import { buildImageSrc, buildImageSrcError, calcPrice, formatCurrency, isNroSegmentoPrecoCuringa } from '@/shared';
import { useClipboard } from '@/shared/clipboard';
import { useCalcPrice, useDbFunctions, useEvents } from '@/talons';
import { PackagesTypesProps, PackagingProductProps, PriceProductProps, ProductProps, TaxSubstitutionProps } from '@/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as S from './styles';

import { RiAddLine, RiSubtractLine } from 'react-icons/ri';

interface Props {
  product: ProductProps;
  price: PriceProductProps;
  isOpenedModal: boolean;
  pack: PackagesTypesProps;
  substTrib: TaxSubstitutionProps[];
  openModal: (val: boolean) => void;
  setPackage: (val: PackagesTypesProps) => void;
  packQty: number;
  setPackQuantity: (val: number) => void;
  hasStock?: any;
}

// Constantes
const PERCENTAGE_LIMITS = { MIN: -5, MAX: 9 };
const DECIMAL_PLACES = 2;
const UNIT_PRICE_DECIMAL_PLACES = 2;
const PRECISION_TOLERANCE = 0.001;

const truncateToDecimal = (num: number, decimalPlaces: number): number => {
  const factor = Math.pow(10, decimalPlaces);
  return Math.trunc(num * factor) / factor;
};

const validateAndNormalize = (value: number): number => {
  return isNaN(value) || !isFinite(value) ? 0 : value;
};

const normalizeDecimalInput = (value: string): string => value.replace(',', '.');

const adjustPercentageToExactLimit = (percentage: number): number => {
  // Verificar se está próximo do limite máximo (9%)
  if (Math.abs(percentage - PERCENTAGE_LIMITS.MAX) <= PRECISION_TOLERANCE) {
    return PERCENTAGE_LIMITS.MAX;
  }

  // Verificar se está próximo do limite mínimo (-5%)
  if (Math.abs(percentage - PERCENTAGE_LIMITS.MIN) <= PRECISION_TOLERANCE) {
    return PERCENTAGE_LIMITS.MIN;
  }

  // Verificar se está próximo de 0%
  if (Math.abs(percentage) <= PRECISION_TOLERANCE) {
    return 0;
  }

  return percentage;
};

// Função aprimorada para verificar se um valor de preço corresponde exatamente a um percentual limite
const getExactPercentageFromPrice = (price: number, initialPrice: number): number | null => {
  if (initialPrice <= 0) return null;

  // Calcular percentual com maior precisão
  const calculatedPercentage = ((price - initialPrice) / initialPrice) * 100;

  // Tolerância menor para verificar correspondência exata de preço (0.5 centavos)
  const priceTolerance = 0.005;

  // Verificar se o preço corresponde exatamente ao limite máximo (9%)
  const maxPrice = initialPrice + (initialPrice * PERCENTAGE_LIMITS.MAX) / 100;
  if (Math.abs(price - maxPrice) <= priceTolerance) {
    return PERCENTAGE_LIMITS.MAX;
  }

  // Verificar se o preço corresponde exatamente ao limite mínimo (-5%)
  const minPrice = initialPrice + (initialPrice * PERCENTAGE_LIMITS.MIN) / 100;
  if (Math.abs(price - minPrice) <= priceTolerance) {
    return PERCENTAGE_LIMITS.MIN;
  }

  // Verificar se o preço corresponde exatamente ao preço inicial (0%)
  if (Math.abs(price - initialPrice) <= priceTolerance) {
    return 0;
  }

  // Verificar por proximidade de percentual calculado
  const adjustedPercentage = adjustPercentageToExactLimit(calculatedPercentage);
  if (adjustedPercentage !== calculatedPercentage) {
    return adjustedPercentage;
  }

  return null;
};

// Função para forçar percentual exato quando no limite
const forceExactPercentageAtLimit = (percentage: number): number => {
  // Se está muito próximo de -5%, forçar para -5%
  if (percentage <= PERCENTAGE_LIMITS.MIN + PRECISION_TOLERANCE && percentage >= PERCENTAGE_LIMITS.MIN - PRECISION_TOLERANCE) {
    return PERCENTAGE_LIMITS.MIN;
  }

  // Se está muito próximo de 9%, forçar para 9%
  if (percentage >= PERCENTAGE_LIMITS.MAX - PRECISION_TOLERANCE && percentage <= PERCENTAGE_LIMITS.MAX + PRECISION_TOLERANCE) {
    return PERCENTAGE_LIMITS.MAX;
  }

  // Se está muito próximo de 0%, forçar para 0%
  if (Math.abs(percentage) <= PRECISION_TOLERANCE) {
    return 0;
  }

  return percentage;
};

// Helper para normalizar saldoAFaturar antigo (quando ele foi salvo como TOTAL em vez de por pack)
const normalizeSaldoAFaturar = (raw: number, quantityCart: number, initialPrice: number) => {
  if (quantityCart > 1 && raw > 0) {
    const perPackCandidate = raw / quantityCart;
    // Se o percentual por pack cabe no limite configurado, assumimos que o valor salvo estava em TOTAL e normalizamos
    const percentCandidate = initialPrice > 0 ? (perPackCandidate / initialPrice) * 100 : 0;
    if (percentCandidate <= PERCENTAGE_LIMITS.MAX + 0.0001) {
      return perPackCandidate;
    }
  }
  return raw; // já está em formato por pack
};

const ModalFlexDiscount = ({
  pack,
  product,
  price,
  substTrib,
  openModal,
  setPackage,
  isOpenedModal,
  setPackQuantity,
  packQty,
  hasStock,
}: Props) => {
  const db = useDbFunctions();
  const { tables } = useTables();
  const { showAlert } = useAlert();
  const { paymentCode } = useGlobals();
  const paymentCodeNumber = useMemo(() => Number(paymentCode) || 0, [paymentCode]);
  const { registerEvent } = useEvents(db);
  const { getPrices } = useCalcPrice();
  const { selectedClient } = useClient();
  const { selectedBrandPair } = useCorporateFlags();
  const { copyToClipboard } = useClipboard();
  const { rcaSaldoFlex } = useUserContext(); // removido setRcaSaldoFlex
  const { currentCartDetails, addProductToCart, updateLastOrderSendTime } = useCart();
  const [isOpenTooltip, setOpenTooltip] = useState(false);
  const { setDiscount, getDiscountProduct, deleteDiscount } = useDiscountFlex();
  const { discountAmountAll, estimatedSaldoFlexClient } = useDiscountAmountFlex();

  const image = useMemo(() => product.IMAGEM || tables.current.imagens?.[product.COD_PRODUTO], [product.IMAGEM, tables, product.COD_PRODUTO]);

  useEffect(() => {
    if (!isOpenTooltip) return;

    showAlert({
      bgType: 'primary',
      bordered: true,
      theme: 'warning',
      title: 'Saldo Flex Indiponível',
      message: `Saldo Flex Indiponível: ${formatCurrency({
        value: (rcaSaldoFlex || 0) + discountAmountAll,
      })}`,
    });
  }, [isOpenTooltip]);

  // Estados consolidados
  const [values, setValues] = useState({
    flex: '0', // Valor para o slider, pode precisar de mais precisão
    unitPrice: '0',
    currentPrice: '0',
    percentageInput: '0',
  });

  const [focusedValues, setFocusedValues] = useState({
    price: 0,
    unitPrice: 0,
    percentage: 0,
  });

  const [appliedDiscounts, setAppliedDiscounts] = useState({} as { [key: number]: number });
  // Estado para saldo a faturar (valor adicional quando o preço aumenta)
  const [saldoAFaturar, setSaldoAFaturar] = useState(0);
  const [originalFlexPercentage, setOriginalFlexPercentage] = useState<number | null>(null);
  const [hasFlexChanged, setHasFlexChanged] = useState(false);

  const productPrices = useMemo(() => getPrices(product), [getPrices, product]);

  const toNumber = useCallback((value: unknown) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);
  const getSegmentCode = useCallback((segmento: any) => {
    return toNumber(segmento?.cod_segmento ?? segmento?.COD_SEGMENTO ?? segmento);
  }, [toNumber]);
  const selectedClientScope = useMemo(() => {
    if (!selectedClient) return selectedClient;
    if (!selectedBrandPair?.codLoja || !selectedBrandPair?.codSegmento) return selectedClient;

    const lojas = Array.isArray(selectedClient.lojas) ? selectedClient.lojas : [];
    const matchedLoja = lojas.find((loja: any) => {
      if (toNumber(loja?.COD_LOJA) !== Number(selectedBrandPair.codLoja)) return false;
      const segmentosLoja = Array.isArray(loja?.COD_SEGMENTO) ? loja.COD_SEGMENTO : [];
      return segmentosLoja.some((segmento: any) => getSegmentCode(segmento) === Number(selectedBrandPair.codSegmento));
    });
    if (!matchedLoja) return selectedClient;

    const segmentosLoja = Array.isArray(matchedLoja?.COD_SEGMENTO) ? matchedLoja.COD_SEGMENTO : [];
    const segmentoAtivo = segmentosLoja.find((segmento: any) => getSegmentCode(segmento) === Number(selectedBrandPair.codSegmento));

    return {
      ...selectedClient,
      ...matchedLoja,
      COD_SEGMENTO: segmentoAtivo ? [segmentoAtivo] : segmentosLoja,
    };
  }, [selectedClient, selectedBrandPair, toNumber, getSegmentCode]);

  const prices = useMemo(() => {
    const clientSegs = (Array.isArray(selectedClientScope?.COD_SEGMENTO) ? selectedClientScope.COD_SEGMENTO : [])
      .map((segmento: any) => getSegmentCode(segmento))
      .filter((segmento): segmento is number => segmento != null);
    const clientLoja = toNumber(selectedClientScope?.COD_LOJA);
    return productPrices.filter((p) => {
      const segmentCode = toNumber(p?.NRO_SEGMENTO);
      const segs =
        segmentCode == null ||
        isNroSegmentoPrecoCuringa(segmentCode) ||
        !clientSegs.length ||
        clientSegs.some((s: number) => s === segmentCode);
      const isPackageMatch =
        pack === 'PK'
          ? p.SG_EMBALAGEM === pack && p.QTD_MULTIPLO_VENDA === packQty
          : p.SG_EMBALAGEM === pack && p.QTD_EMBALAGEM === packQty;
      const priceLoja = toNumber(p?.COD_LOJA);
      const lojaMatch = clientLoja == null || priceLoja == null || priceLoja === clientLoja;
      return isPackageMatch && lojaMatch && segs;
    });
  }, [productPrices, pack, packQty, selectedClientScope, getSegmentCode, toNumber]);

  const initialPrice = useMemo(
    () =>
      prices?.find((p) => Number(p.NROCONDICAOPAGTO) === paymentCodeNumber)?.VLR_FINAL ||
      Number(price?.VLR_FINAL || price?.VLR_PRECO_CALCULADO || price?.VLR_PRECO) ||
      0,
    [prices, paymentCodeNumber, price]
  );

  // Calcular valor máximo e mínimo permitido para o input de preço total
  const priceLimits = useMemo(() => {
    if (initialPrice <= 0) return { min: 0, max: 0 };

    const maxPrice = initialPrice + (initialPrice * PERCENTAGE_LIMITS.MAX) / 100;
    const minPrice = initialPrice + (initialPrice * PERCENTAGE_LIMITS.MIN) / 100;

    return {
      min: truncateToDecimal(minPrice, DECIMAL_PLACES),
      max: truncateToDecimal(maxPrice, DECIMAL_PLACES),
    };
  }, [initialPrice]);

  const qtdEmb = useMemo(() => {
    const emb = product?.EMBALAGEM?.find((e: PackagingProductProps) => {
      const isPackageMatch =
        pack === 'PK'
          ? e.SG_EMBALAGEM === pack && e.QTD_MULTIPLO_VENDA === packQty
          : e.SG_EMBALAGEM === pack && e?.QTD_EMBALAGEM === packQty;
      return isPackageMatch;
    }) as PackagingProductProps || {};

    const isMultiple = emb?.QTD_MULTIPLO_VENDA > 1 && pack === 'PK';
    return isMultiple ? emb?.QTD_MULTIPLO_VENDA : emb?.QTD_EMBALAGEM || 0;
  }, [product?.EMBALAGEM, pack, packQty]);

  // Calcular valor máximo e mínimo permitido para o input de preço unitário (com 3 casas decimais)
  const unitPriceLimits = useMemo(() => {
    if (qtdEmb <= 0 || priceLimits.max === 0) return { min: 0, max: 0 };

    return {
      min: truncateToDecimal(priceLimits.min / qtdEmb, UNIT_PRICE_DECIMAL_PLACES),
      max: truncateToDecimal(priceLimits.max / qtdEmb, UNIT_PRICE_DECIMAL_PLACES),
    };
  }, [priceLimits, qtdEmb]);

  const packageCode = useMemo(() => pack?.split('_')[0] ?? pack, [pack]);

  const ctxDiscountProduct = useMemo(() => {
    const discount = getDiscountProduct({
      package: packageCode as PackagesTypesProps,
      quantity: qtdEmb,
      productId: product.COD_PRODUTO,
    });

    return {
      price: discount?.discountPrice?.[paymentCodeNumber] || 0,
      percentual: discount?.percentual || 0,
      saldoAFaturar: discount?.saldoAFaturar || 0, // <--- NOVO
    };
  }, [getDiscountProduct, packageCode, qtdEmb, product.COD_PRODUTO, paymentCodeNumber]);

  const calcPrices = useCallback(
    (curFlex: number) => {
      const basePrices = prices.length ? prices : price ? [price as any] : [];
      const newPrices = basePrices.map((p) => ({
        ...p,
        VLR_PRECO: p.VLR_PRECO + (p.VLR_PRECO / 100) * curFlex,
      }));

      const calculated = calcPrice(selectedClientScope, newPrices, substTrib);
      const pricesToReduce =
        calculated?.length > 0
          ? calculated
          : newPrices.map((p) => ({
              ...p,
              VLR_FINAL: Number(p?.VLR_FINAL || p?.VLR_PRECO_CALCULADO || p?.VLR_PRECO || 0),
            }));

      return pricesToReduce.reduce(
        (acc, p) => ({
          ...acc,
          [p.NROCONDICAOPAGTO]: p?.VLR_FINAL || 0,
        }),
        {} as { [key: string]: number }
      );
    },
    [selectedClientScope, prices, substTrib, price]
  );

  // Função otimizada para calcular valores interconectados com precisão corrigida
  const calculateInterconnectedValues = useCallback(
    (changedField: 'price' | 'percentage' | 'unitPrice', newValue: number) => {
      let calculatedPrice = 0;
      let calculatedPercentage = 0;
      let calculatedUnitPrice = 0;

      switch (changedField) {
        case 'price':
          calculatedPrice = Math.max(priceLimits.min, Math.min(priceLimits.max, newValue));

          if (initialPrice > 0) {
            const exactPercentage = getExactPercentageFromPrice(calculatedPrice, initialPrice);

            if (exactPercentage !== null) {
              calculatedPercentage = exactPercentage;
            } else {
              calculatedPercentage = ((calculatedPrice - initialPrice) / initialPrice) * 100;
              calculatedPercentage = adjustPercentageToExactLimit(calculatedPercentage);
              calculatedPercentage = forceExactPercentageAtLimit(calculatedPercentage);
              calculatedPercentage = Math.max(PERCENTAGE_LIMITS.MIN, Math.min(PERCENTAGE_LIMITS.MAX, calculatedPercentage));
            }
          }

          if (qtdEmb > 0) {
            calculatedUnitPrice = calculatedPrice / qtdEmb;
          }
          break;

        case 'percentage':
          // Primeiro, forçar percentual exato se estiver no limite
          calculatedPercentage = forceExactPercentageAtLimit(newValue);

          // Depois, ajustar para valor exato quando próximo dos limites
          calculatedPercentage = adjustPercentageToExactLimit(calculatedPercentage);

          // Garantir que está dentro dos limites
          calculatedPercentage = Math.max(PERCENTAGE_LIMITS.MIN, Math.min(PERCENTAGE_LIMITS.MAX, calculatedPercentage));

          const pricesFromPercentage = calcPrices(calculatedPercentage);
          calculatedPrice = pricesFromPercentage[paymentCodeNumber] || 0;

          if (qtdEmb > 0) {
            calculatedUnitPrice = calculatedPrice / qtdEmb;
          }
          break;

        case 'unitPrice':
          // Limitar o preço unitário aos valores máximo e mínimo permitidos
          calculatedUnitPrice = Math.max(unitPriceLimits.min, Math.min(unitPriceLimits.max, newValue));
          calculatedPrice = calculatedUnitPrice * qtdEmb;

          if (initialPrice > 0) {
            // Primeiro, verificar se o preço corresponde exatamente a um percentual limite
            const exactPercentage = getExactPercentageFromPrice(calculatedPrice, initialPrice);

            if (exactPercentage !== null) {
              calculatedPercentage = exactPercentage;
            } else {
              calculatedPercentage = ((calculatedPrice - initialPrice) / initialPrice) * 100;

              calculatedPercentage = adjustPercentageToExactLimit(calculatedPercentage);

              calculatedPercentage = forceExactPercentageAtLimit(calculatedPercentage);

              calculatedPercentage = Math.max(PERCENTAGE_LIMITS.MIN, Math.min(PERCENTAGE_LIMITS.MAX, calculatedPercentage));
            }
          }
          break;
      }

      return {
        price: calculatedPrice, // Manter precisão total para cálculos internos
        percentage: calculatedPercentage, // Manter precisão total para cálculos internos
        unitPrice: calculatedUnitPrice, // Manter precisão total para cálculos internos
      };
    },
    [initialPrice, qtdEmb, calcPrices, paymentCodeNumber, priceLimits, unitPriceLimits]
  );

  const updateAppliedDiscounts = useCallback(
    (percentage: number, exactPrice?: number) => {
      const pricesFromPercentage = calcPrices(percentage);
      const paymentConditions = Array.isArray(selectedClientScope?.COD_PAGAMENTO) ? selectedClientScope.COD_PAGAMENTO : [];
      const newDiscounts = paymentConditions.reduce(
        (acc, curr) => {
          if (exactPrice !== undefined && Number(curr.cod_condicao) === paymentCodeNumber) {
            acc[curr.cod_condicao] = truncateToDecimal(exactPrice, DECIMAL_PLACES);
          } else {
            acc[curr.cod_condicao] = pricesFromPercentage[curr.cod_condicao];
          }
          return acc;
        },
        {} as { [key: number]: number }
      );

      setAppliedDiscounts(newDiscounts);
    },
    [calcPrices, selectedClientScope?.COD_PAGAMENTO, paymentCodeNumber]
  );

  const updateAllValues = useCallback(
    (calculated: { price: number; percentage: number; unitPrice: number }, exactPrice?: number) => {
      setValues({
        flex: `${calculated.percentage}`, // O slider pode precisar de mais precisão aqui
        percentageInput: `${calculated.percentage.toFixed(DECIMAL_PLACES)}`, // Formatado para exibição
        currentPrice: `${calculated.price.toFixed(DECIMAL_PLACES)}`, // Formatado para exibição
        unitPrice: `${calculated.unitPrice.toFixed(UNIT_PRICE_DECIMAL_PLACES)}`, // Formatado para exibição
      });

      updateAppliedDiscounts(calculated.percentage, exactPrice);
    },
    [updateAppliedDiscounts]
  );

  const quantityCart = useMemo(() => {
    const [pck] = pack?.split('_');
    return (
      currentCartDetails?.items?.find((i) => i.uid === `${product.COD_PRODUTO}-${pck}-${qtdEmb}`)
        ?.quantity || 0
    );
  }, [currentCartDetails, product.COD_PRODUTO, qtdEmb, pack]);

  const dynamicDiscountLimits = useMemo(() => {
    if (initialPrice <= 0) {
      return {
        minPercentage: 0,
        minPrice: initialPrice,
        minUnitPrice: initialPrice && qtdEmb > 0 ? initialPrice / qtdEmb : 0,
      };
    }

    const qtd = quantityCart || 1;

    const diffCtx = ctxDiscountProduct.price ? ctxDiscountProduct.price - initialPrice : 0;
    const impactoDescontoAnterior = diffCtx < 0 ? diffCtx * qtd : 0;

    const saldoDisponivel = (rcaSaldoFlex || 0) + discountAmountAll - impactoDescontoAnterior;

    if (saldoDisponivel <= 0) {
      return {
        minPercentage: 0,
        minPrice: initialPrice,
        minUnitPrice: qtdEmb > 0 ? initialPrice / qtdEmb : 0,
      };
    }

    const diffAtualMin = -saldoDisponivel / qtd;
    const percentageMinBySaldo = (diffAtualMin / initialPrice) * 100;

    const effectiveMinPercentage = Math.max(percentageMinBySaldo, PERCENTAGE_LIMITS.MIN);

    const minPriceBySaldo = initialPrice + (initialPrice * effectiveMinPercentage) / 100;

    return {
      minPercentage: truncateToDecimal(effectiveMinPercentage, DECIMAL_PLACES),
      minPrice: truncateToDecimal(minPriceBySaldo, DECIMAL_PLACES),
      minUnitPrice: qtdEmb > 0 ? truncateToDecimal(minPriceBySaldo / qtdEmb, UNIT_PRICE_DECIMAL_PLACES) : 0,
    };
  }, [initialPrice, qtdEmb, quantityCart, ctxDiscountProduct.price, discountAmountAll, rcaSaldoFlex]);

  const diffAtual = useMemo(() => Number(values.currentPrice) - initialPrice, [values.currentPrice, initialPrice]);

  const saldoFlexPrevisto = useMemo(() => {
    if (diffAtual < 0) {
      const qtd = quantityCart || 1;
      const impactoDescontoAtual = diffAtual * qtd;
      const diffCtx = ctxDiscountProduct.price ? ctxDiscountProduct.price - initialPrice : 0;
      const impactoDescontoAnterior = diffCtx < 0 ? diffCtx * qtd : 0;

      return (rcaSaldoFlex || 0) + discountAmountAll - impactoDescontoAnterior + impactoDescontoAtual;
    }
    return rcaSaldoFlex || 0;
  }, [diffAtual, quantityCart, rcaSaldoFlex, discountAmountAll, ctxDiscountProduct.price, initialPrice]);

  useEffect(() => {
    if (!isOpenedModal) return;

    if (initialPrice > 0) {
      if (ctxDiscountProduct.price > 0 && ctxDiscountProduct.percentual < 0) {
        // DESCONTO salvo
        const initialPercentage = ctxDiscountProduct.percentual;
        const initialPriceFormatted = ctxDiscountProduct.price.toFixed(DECIMAL_PLACES);
        const initialUnitPrice = (ctxDiscountProduct.price / qtdEmb).toFixed(UNIT_PRICE_DECIMAL_PLACES);

        setValues({
          currentPrice: initialPriceFormatted,
          flex: `${initialPercentage}`,
          percentageInput: `${initialPercentage.toFixed(DECIMAL_PLACES)}`,
          unitPrice: initialUnitPrice,
        });
        setSaldoAFaturar(0);
        setOriginalFlexPercentage(initialPercentage);
        setHasFlexChanged(false);
      } else if (ctxDiscountProduct.saldoAFaturar > 0) {
        const diffPerPack = normalizeSaldoAFaturar(ctxDiscountProduct.saldoAFaturar, quantityCart || 1, initialPrice);
        let increasedPrice = initialPrice + diffPerPack;

        let percentage =
          ctxDiscountProduct.percentual > 0
            ? ctxDiscountProduct.percentual
            : ((increasedPrice - initialPrice) / initialPrice) * 100;

        if (percentage > PERCENTAGE_LIMITS.MAX) {
          percentage = PERCENTAGE_LIMITS.MAX;
          increasedPrice = initialPrice + (initialPrice * percentage) / 100;
        }

        const unitPrice = qtdEmb > 0 ? (increasedPrice / qtdEmb).toFixed(UNIT_PRICE_DECIMAL_PLACES) : '0.00';

        setValues({
          currentPrice: increasedPrice.toFixed(DECIMAL_PLACES),
          flex: `${percentage}`,
          percentageInput: percentage.toFixed(DECIMAL_PLACES),
          unitPrice,
        });

        setSaldoAFaturar(diffPerPack); // sempre POR PACK
        setOriginalFlexPercentage(percentage);
        setHasFlexChanged(false);
      } else {
        // Neutro
        const initialPriceFormatted = initialPrice.toFixed(DECIMAL_PLACES);
        const initialUnitPrice = (initialPrice / qtdEmb).toFixed(UNIT_PRICE_DECIMAL_PLACES);
        setValues({
          currentPrice: initialPriceFormatted,
          flex: '0',
          percentageInput: '0',
          unitPrice: initialUnitPrice,
        });
        setSaldoAFaturar(0);
        setOriginalFlexPercentage(0);
        setHasFlexChanged(false);
      }
    } else {
      setValues({
        currentPrice: '0.00',
        flex: '0',
        percentageInput: '0',
        unitPrice: '0.000',
      });
      setSaldoAFaturar(0);
      setOriginalFlexPercentage(0);
      setHasFlexChanged(false);
    }
  }, [
    isOpenedModal,
    initialPrice,
    ctxDiscountProduct.price,
    ctxDiscountProduct.percentual,
    ctxDiscountProduct.saldoAFaturar,
    qtdEmb,
    quantityCart,
  ]);

  // Substituir o useEffect que recalcula saldoAFaturar pelo abaixo (remove duplicações)
  useEffect(() => {
    const diffUnit = Number(values.currentPrice) - initialPrice; // diferença POR PACK

    if (diffUnit < 0) {
      // Desconto: zera qualquer saldo a faturar
      if (saldoAFaturar !== 0) setSaldoAFaturar(0);
      return;
    }

    if (diffUnit === 0) {
      // Mantém valor salvo apenas se existir no contexto
      if (ctxDiscountProduct.saldoAFaturar > 0) {
        const normalized = normalizeSaldoAFaturar(ctxDiscountProduct.saldoAFaturar, quantityCart || 1, initialPrice);
        if (saldoAFaturar !== normalized) setSaldoAFaturar(normalized);
      } else if (saldoAFaturar !== 0) {
        setSaldoAFaturar(0);
      }
      return;
    }

    // ACRÉSCIMO: sempre trabalhar com valor por pack (diffUnit)
    if (saldoAFaturar !== diffUnit) setSaldoAFaturar(diffUnit);
  }, [values.currentPrice, initialPrice, ctxDiscountProduct.saldoAFaturar, saldoAFaturar, quantityCart]);

  const setDiscountPrice = useCallback(async () => {
    if ((rcaSaldoFlex || 0) < 0 ? saldoFlexPrevisto < (rcaSaldoFlex || 0) : saldoFlexPrevisto < 0) {
      setOpenTooltip(true);
      setTimeout(() => setOpenTooltip(false), 5000);
      return;
    }
    
    const optionUid = `${product?.COD_PRODUTO}-${pack}-${findQtdPack()}`;
    registerEvent({
      tipoEvento: 'FLEX',
      codProduto: product?.COD_PRODUTO,
      obsEvento: `[setDiscountPrice]: UID: ${optionUid} | RCASALDOFLEX: ${rcaSaldoFlex} | percentual: ${values.flex}`,
    });

    // Se usuário trouxe o preço de volta exatamente ao inicial (percentual 0) e havia algo aplicado,
    // removemos o registro de flex (desconto ou acréscimo) do contexto.
    const tinhaAlgoAplicado =
      ctxDiscountProduct.percentual !== 0 || ctxDiscountProduct.saldoAFaturar > 0 || ctxDiscountProduct.price > 0;

    updateLastOrderSendTime();
    if (diffAtual === 0 && tinhaAlgoAplicado) {
      deleteDiscount({
        productId: product.COD_PRODUTO,
        package: packageCode,
        quantity: qtdEmb,
      } as any);
      setSaldoAFaturar(0);
      openModal(false);
      return;
    }

    if (diffAtual < 0) {
      // DESCONTO (consome flex)
      setDiscount({
        package: packageCode,
        quantity: qtdEmb,
        percentual: Number(values.flex), // negativo
        price: appliedDiscounts,
        productId: product.COD_PRODUTO,
        saldoAFaturar: 0,
      });
    } else if (diffAtual > 0 && saldoAFaturar > 0) {
      setDiscount({
        package: packageCode,
        quantity: qtdEmb,
        productId: product.COD_PRODUTO,
        saldoAFaturar: diffAtual,
        price: appliedDiscounts,
        percentual: Number(values.flex),
      });
    }

    openModal(false);
  }, [
    diffAtual,
    saldoFlexPrevisto,
    rcaSaldoFlex,
    setDiscount,
    deleteDiscount,
    packageCode,
    qtdEmb,
    values.flex,
    appliedDiscounts,
    product.COD_PRODUTO,
    saldoAFaturar,
    initialPrice,
    ctxDiscountProduct.percentual,
    ctxDiscountProduct.saldoAFaturar,
    ctxDiscountProduct.price,
    openModal,
  ]);

  const findQtdPack = useCallback(() => {
    const packing = product?.EMBALAGEM?.find((p) => {
      const isMultipleMatch =
        p.QTD_MULTIPLO_VENDA > 1
          ? p.SG_EMBALAGEM === pack && p.QTD_MULTIPLO_VENDA === packQty
          : p.SG_EMBALAGEM === pack && p.QTD_EMBALAGEM === packQty;
      return isMultipleMatch;
    }) as PackagingProductProps || {};

    return packing?.QTD_MULTIPLO_VENDA && packing?.QTD_MULTIPLO_VENDA > 1 ? packing?.QTD_MULTIPLO_VENDA : packing?.QTD_EMBALAGEM;
  }, [product?.EMBALAGEM, pack, packQty]);

  const handleChangeFlex = useCallback(
    (val: number) => {
      let adjusted = val;

      // Normalizar valores muito próximos de zero
      if (Math.abs(adjusted) <= PRECISION_TOLERANCE) {
        adjusted = 0;
      }

      if (adjusted < 0) {
        adjusted = Math.max(adjusted, dynamicDiscountLimits.minPercentage);
      }
      adjusted = Math.min(adjusted, PERCENTAGE_LIMITS.MAX);

      const calculated = calculateInterconnectedValues('percentage', adjusted);
      if (calculated.price < dynamicDiscountLimits.minPrice) {
        const corrected = calculateInterconnectedValues('price', dynamicDiscountLimits.minPrice);
        updateAllValues(corrected);
        return;
      }
      updateAllValues(calculated);
    },
    [calculateInterconnectedValues, updateAllValues, dynamicDiscountLimits.minPercentage, dynamicDiscountLimits.minPrice]
  );

  const handleInputChange = useCallback(
    (field: keyof typeof values) => (val: string) => {
      const newValue = normalizeDecimalInput(val);
      setValues((prev) => ({ ...prev, [field]: newValue }));
    },
    []
  );

  const handleInputFocus = useCallback((field: keyof typeof focusedValues, currentValue: string) => {
    const numValue = validateAndNormalize(Number(currentValue));
    setFocusedValues((prev) => ({ ...prev, [field]: numValue }));

    const fieldMap = {
      price: 'currentPrice',
      unitPrice: 'unitPrice',
      percentage: 'percentageInput',
    };

    const targetField = fieldMap[field] as keyof typeof values;
    setValues((prev) => ({ ...prev, [targetField]: '' }));
  }, []);

  const handleInputBlur = useCallback(
    (field: 'price' | 'percentage' | 'unitPrice', value: string) => {
      const newValue = Number(value);
      const focusedValue = focusedValues[field];

      if (isNaN(newValue) || value === '') {
        const validFocusedValue = validateAndNormalize(focusedValue);
        let formattedValue: string;

        if (field === 'percentage') {
          formattedValue = validFocusedValue.toFixed(DECIMAL_PLACES);
        } else if (field === 'unitPrice') {
          formattedValue = validFocusedValue.toFixed(UNIT_PRICE_DECIMAL_PLACES);
        } else {
          formattedValue = validFocusedValue.toFixed(DECIMAL_PLACES);
        }

        const fieldMap = {
          price: 'currentPrice',
          percentage: 'percentageInput',
          unitPrice: 'unitPrice',
        };

        setValues((prev) => ({ ...prev, [fieldMap[field]]: formattedValue }));
        return;
      }

      if (field === 'price' && !initialPrice) return;

      let limitedValue = newValue;

      switch (field) {
        case 'price': {
          const effectiveMinPrice = Math.max(priceLimits.min, dynamicDiscountLimits.minPrice);
          limitedValue = Math.max(effectiveMinPrice, Math.min(priceLimits.max, newValue));
          break;
        }
        case 'percentage': {
          const effectiveMinPercentage = dynamicDiscountLimits.minPercentage;
          limitedValue = Math.max(effectiveMinPercentage, Math.min(PERCENTAGE_LIMITS.MAX, newValue));
          break;
        }
        case 'unitPrice': {
          const effectiveMinUnit = Math.max(unitPriceLimits.min, dynamicDiscountLimits.minUnitPrice);
          limitedValue = Math.max(effectiveMinUnit, Math.min(unitPriceLimits.max, newValue));
          break;
        }
      }

      const calculated = calculateInterconnectedValues(field, limitedValue);

      if (calculated.price < dynamicDiscountLimits.minPrice) {
        const corrected = calculateInterconnectedValues('price', dynamicDiscountLimits.minPrice);
        updateAllValues(corrected, field === 'price' ? dynamicDiscountLimits.minPrice : undefined);
        return;
      }

      updateAllValues(calculated, field === 'price' ? limitedValue : undefined);
    },
    [
      focusedValues,
      initialPrice,
      calculateInterconnectedValues,
      updateAllValues,
      priceLimits,
      unitPriceLimits,
      dynamicDiscountLimits.minPrice,
      dynamicDiscountLimits.minPercentage,
      dynamicDiscountLimits.minUnitPrice,
    ]
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, type: 'price' | 'percentage' | 'unitPrice') => {
    if (e.key === 'Enter') {
      e?.preventDefault();
      handleInputBlur(type, (e.target as HTMLInputElement).value);
    }
  }, []);

  const setPriceAndAddToCart = useCallback(() => {
    if ((rcaSaldoFlex || 0) < 0 ? saldoFlexPrevisto < (rcaSaldoFlex || 0) : saldoFlexPrevisto < 0) {
      setOpenTooltip(true);
      setTimeout(() => setOpenTooltip(false), 5000);
      return;
    }

    addProductToCart({
      quantity: 1,
      sku: product?.COD_PRODUTO,
      category_id: product?.COD_CATEGORIA,
      option_uid: `${pack}_${findQtdPack()}`,
      uid: `${product?.COD_PRODUTO}-${pack}-${findQtdPack()}`,
    });
    setDiscountPrice();
  }, [setDiscountPrice, addProductToCart, findQtdPack, saldoFlexPrevisto, product, pack]);

  const changeFlexByStep = useCallback(
    (delta: number) => {
      const current = Number(values.flex);
      if (Number.isNaN(current)) return;

      // Regra: se houver casas decimais, primeiro ir para o inteiro "na direção" do botão.
      // - incrementar: vai para o próximo inteiro acima (ceil)
      // - decrementar: vai para o próximo inteiro abaixo (floor)
      // Se já for inteiro, aí sim soma/subtrai 1.
      const isInteger = Number.isInteger(current);

      let nextInt: number;
      if (delta > 0) {
        nextInt = isInteger ? current + 1 : Math.ceil(current);
      } else {
        nextInt = isInteger ? current - 1 : Math.floor(current);
      }

      if (nextInt < PERCENTAGE_LIMITS.MIN || nextInt > PERCENTAGE_LIMITS.MAX) return;

      handleChangeFlex(nextInt);
    },
    [values.flex, handleChangeFlex],
  );
  
  const handleDecrementFlex = useCallback(() => {
    changeFlexByStep(-1);
  }, [changeFlexByStep]);
  
  const handleIncrementFlex = useCallback(() => {
    changeFlexByStep(1);
  }, [changeFlexByStep]);

  useEffect(() => {
    if (originalFlexPercentage === null) return;
    const currentPerc = Number(values.flex);
    if (!Number.isNaN(currentPerc)) {
      if (currentPerc === 0 && originalFlexPercentage !== 0) {
        setHasFlexChanged(true);
      } else {
        setHasFlexChanged(currentPerc !== originalFlexPercentage);
      }
    }
  }, [values.flex, originalFlexPercentage]);

  const ean = useMemo(() => product?.ATRIBUTOS?.find((at) => at.ean_grp)?.ean_grp, [product]);
  const sku = useMemo(() => product.COD_PRODUTO, [product]);

  return (
    <S.ModalContent>
      <S.ModalTitle>Aplicar desconto no produto</S.ModalTitle>

      <S.ContentProduct>
        <S.ImageProductContent>
          <S.ImageProduct src={buildImageSrc(image)} onError={buildImageSrcError} />
        </S.ImageProductContent>

        <S.ProductInfos>
          <S.Row>
            <S.ProductCode
              onClick={() => {
                copyToClipboard({
                  value: sku.toString(),
                  title: `Cód. <b>${sku}</b> copiado com sucesso`,
                });
              }}
            >
              {sku}
            </S.ProductCode>
            <S.ProductCode
              onClick={() => {
                copyToClipboard({
                  value: ean || '',
                  title: `EAN <b>${ean}</b> copiado com sucesso`,
                });
              }}
            >
              {ean}
            </S.ProductCode>
          </S.Row>

          <S.ProductDesc>{product.DESC_PRODUTO}</S.ProductDesc>
          <Packaging
            setPackQuantity={setPackQuantity}
            packQty={packQty}
            showQtd
            pack={pack}
            canDisablePack={false}
            setPackage={setPackage}
            product={product}
            autoSelectFirst={false}
            fullBorder={true}
          />
        </S.ProductInfos>
      </S.ContentProduct>

      <S.InstructionLAbel>Escolha abaixo a porcentagem que deseja alterar no valor do produto</S.InstructionLAbel>

      <S.PricesWrapper>
        <S.PricesLabelsWrapper>
          <S.ProductPriceLabel>Valor do produto</S.ProductPriceLabel>
          <S.ProductPriceLabel>Percentual</S.ProductPriceLabel>
          <S.ProductPriceLabel>Valor por unidade</S.ProductPriceLabel>
        </S.PricesLabelsWrapper>

        <S.InputPricesWrapper>
          <S.ProductTotalPrice>
            <S.Cifrao>R$</S.Cifrao>
            <S.RelativeValue>
              <S.InputProductPrice
                type="number"
                step={'0.01'}
                min={priceLimits.min}
                max={priceLimits.max}
                value={values.currentPrice}
                pattern="[0-9.]*"
                onFocus={(event: any) => handleInputFocus('price', event.target.value)}
                onBlur={(event: any) => handleInputBlur('price', event.target.value)}
                onChange={(event: any) => handleInputChange('currentPrice')(event.target.value)}
                onKeyDown={(event: any) => handleKeyDown(event, 'price')}
                id={`modal-flex-discount-input-product-price-${product.COD_PRODUTO}`}
              />
            </S.RelativeValue>
          </S.ProductTotalPrice>

          <S.ProductPercentagePrice>
            <S.RelativeValue>
              <S.InputProductPercentage
                type="number"
                step={'0.01'}
                min={PERCENTAGE_LIMITS.MIN}
                max={PERCENTAGE_LIMITS.MAX}
                value={values.percentageInput}
                pattern="[0-9.-]*"
                onFocus={(event: any) => handleInputFocus('percentage', event.target.value)}
                onBlur={(event: any) => handleInputBlur('percentage', event.target.value)}
                onChange={(event: any) => handleInputChange('percentageInput')(event.target.value)}
                onKeyDown={(event: any) => handleKeyDown(event, 'percentage')}
                id={`modal-flex-discount-input-percentage-${product.COD_PRODUTO}`}
              />
              <S.PercentageSymbol>%</S.PercentageSymbol>
            </S.RelativeValue>
          </S.ProductPercentagePrice>

          <S.ProductUnitPrice>
            <S.Cifrao>R$</S.Cifrao>
            <S.RelativeValue>
              <S.InputProductUnitPrice
                type="number"
                step={'0.01'}
                min={unitPriceLimits.min}
                max={unitPriceLimits.max}
                value={values.unitPrice}
                pattern="[0-9.]*"
                readOnly
                id={`modal-flex-discount-input-unit-price-${product.COD_PRODUTO}`}
              />
            </S.RelativeValue>
          </S.ProductUnitPrice>
        </S.InputPricesWrapper>
      </S.PricesWrapper>

      <S.SliderSection>
        <S.SliderContainer>
          <S.SliderButton 
            onClick={handleDecrementFlex} 
            disabled={Number(values.flex) === PERCENTAGE_LIMITS.MIN} 
            className="slider-button-minus" 
            data-test="modal-flex-discount-slider-button-minus">
            <RiSubtractLine />
          </S.SliderButton>
          <S.SliderWrapper>
            <InputRange
              step={'0.01'}
              value={Number(values.flex)}
              setValue={handleChangeFlex}
              id={`modal-flex-discount-input-range-${product.COD_PRODUTO}`}
            />
          </S.SliderWrapper>
          <S.SliderButton 
            onClick={handleIncrementFlex} 
            disabled={Number(values.flex) === PERCENTAGE_LIMITS.MAX} 
            className="slider-button-plus" 
            data-test="modal-flex-discount-slider-button-plus">
            <RiAddLine />
          </S.SliderButton>

        </S.SliderContainer>
      </S.SliderSection>

      <S.BalanceContent>
        <S.Balance>
          Saldo Flex (corrente):
          <S.BalanceValue>
            <S.BalanceValueEmphasis $negative={estimatedSaldoFlexClient < 0}>
              {formatCurrency({ value: estimatedSaldoFlexClient })}
            </S.BalanceValueEmphasis>
          </S.BalanceValue>
        </S.Balance>

        {diffAtual < 0 && hasFlexChanged && (
          <S.Balance>
            Saldo Flex após aplicar:
            <S.BalanceValue>
              <S.BalanceValueEmphasis $negative={saldoFlexPrevisto < 0}>
                {formatCurrency({ value: saldoFlexPrevisto })}
              </S.BalanceValueEmphasis>
            </S.BalanceValue>
          </S.Balance>
        )}

        {saldoAFaturar > 0 && (
          <S.Balance>
            Saldo liberado após faturar:
            <S.BalanceValue>
              <S.BalanceValueEmphasis>{formatCurrency({ value: saldoAFaturar })}</S.BalanceValueEmphasis>
            </S.BalanceValue>
          </S.Balance>
        )}

        <ToolTip direction="top" show={isOpenTooltip} displayMode="default" text="Saldo Flex Indisponível">
          {quantityCart > 0 ? (
            // Quando já existe quantidade no carrinho, permitir apenas aplicar o flex
            <S.ApplyButton theme="dark" $inactive={false} onClick={setDiscountPrice} data-test="modal-flex-discount-apply-button">
              Aplicar
            </S.ApplyButton>
          ) : hasStock != false ? (
            // Sem itens no carrinho: permitir aplicar e adicionar, desde que haja estoque para a embalagem
            <S.ApplyButton
              theme="green"
              onClick={setPriceAndAddToCart}
              data-test="modal-flex-discount-apply-and-add-to-cart-button"
            >
              Aplicar e adicionar ao carrinho
            </S.ApplyButton>
          ) : (
            // Sem itens no carrinho e sem estoque da embalagem selecionada
            <S.ApplyButton disabled>Embalagem sem estoque</S.ApplyButton>
          )}
        </ToolTip>
      </S.BalanceContent>
    </S.ModalContent>
  );
};

export default ModalFlexDiscount;
